import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { NAV, PLATFORM_GROUP } from "./navConfig";
import { useAuth } from "@/context/AuthContext";
import { AureloLogo, AureloMark } from "@/components/Logo";
import { CodeLabsLogo, DevelopedBy } from "@/components/CodeLabsLogo";
import { cn } from "@/lib/utils";
import { ChevronsLeft } from "lucide-react";

export const SidebarNav = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const { user } = useAuth();
  const groups = user?.is_super_admin ? [...NAV, PLATFORM_GROUP] : NAV;
  return (
    <aside
      data-testid="sidebar"
      className={cn("hidden md:flex flex-col bg-[#0B102B] text-slate-200 transition-[width] duration-200 shrink-0 h-screen sticky top-0",
        collapsed ? "w-[80px]" : "w-[264px]")}
    >
      <div className={cn("flex items-center h-16 border-b border-white/5", collapsed ? "justify-center px-2" : "justify-between px-4")}>
        {collapsed ? (
          <button
            onClick={() => setCollapsed(false)}
            data-testid="sidebar-toggle"
            title="Expand sidebar"
            aria-label="Expand sidebar"
            className="flex items-center justify-center h-11 w-11 rounded-xl hover:bg-white/5 transition-colors"
          >
            <AureloMark size={28} light />
          </button>
        ) : (
          <>
            <AureloLogo size={26} light />
            <button
              onClick={() => setCollapsed(true)}
              data-testid="sidebar-toggle"
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
              className="text-slate-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/5"
            >
              <ChevronsLeft className="h-5 w-5" />
            </button>
          </>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto sidebar-scroll px-2.5 py-3">
        {groups.map((group, gi) => (
          <div key={gi} className="mb-1">
            {group.label && !collapsed && (
              <div className="px-3 pt-4 pb-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">{group.label}</div>
            )}
            {group.label && collapsed && gi > 0 && <div className="my-2.5 mx-3 border-t border-white/5" />}
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={item.label}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  cn("group flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-semibold transition-all mb-0.5 relative",
                    collapsed && "justify-center px-0",
                    isActive ? "bg-white/10 text-white ring-1 ring-white/10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03)]" : "text-slate-300/90 hover:bg-white/5 hover:text-white")
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && !collapsed && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full aurelo-gradient" />}
                    <span className={cn("flex items-center justify-center rounded-lg transition-colors", collapsed ? "h-9 w-9" : "h-7 w-7", isActive && "aurelo-gradient shadow-[0_4px_12px_rgba(124,58,237,0.35)]")}>
                      <item.icon className={cn(collapsed ? "h-[22px] w-[22px]" : "h-[19px] w-[19px]", "shrink-0", isActive ? "text-white" : "")} strokeWidth={2} />
                    </span>
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="border-t border-white/5 px-4 py-4 shrink-0">
        {collapsed ? (
          <div className="flex justify-center"><CodeLabsLogo iconOnly height={26} /></div>
        ) : (
          <DevelopedBy labelColor="#64748B" height={22} />
        )}
      </div>
    </aside>
  );
};
